<template>
  <div class="file-uploader-input img-crop-input flex align-center gap10">
    <div class="buttons-container flex column space-between height-all gap10">
      <input type="file" ref="inputEl" hidden @change="chooseFile" :accept="accept"/>
      <template v-if="!isLoading">
        <div v-if="imgToShow" class="text-ph"></div>
        <button @click.prevent.stop="clickInput" class="btn big primary_">{{$t('chooseFile')}}</button>
        <button v-if="imgToShow" @click.prevent.stop="clear" :title="$t('clear')" class="btn clear underline width-content danger_ round_">
          <!-- <div v-html="svgs.x" class="svg-parrent"></div> -->
          {{$t('clear')}}
        </button>
      </template>
      <div v-else class="loader-wrapper">
        <MiniLoader/>
      </div>
    </div>
    <div class="img-container">
      <img class="val-img" :title="value?.[0]?.title" :src="imgToShow" :alt="value?.[0]?.title || $t('clickToUploadFile')" @click="clickInput" @load="onImageLoad" @error="onImageError"/>
      <div v-if="imageLoading" class="image-loader-overlay">
        <MiniLoader/>
      </div>
    </div>

    <Modal :fullScreen="true" v-if="imgBase64ToCrop" @close="clear">
      <div class="crop-modal flex column gap15">
        <!-- <img :src="imgBase64ToCrop" alt=""> -->

        <div class="cropper-container">
          <!-- <img :src="imgBase64ToCrop" ref="imageRef" alt="Image to crop" /> -->
          <VueCropper
            ref="cropper"
            :src="imgBase64ToCrop"
            preview=".preview"
            :viewMode="1"
            :responsive="false"
            :restore="false"
            :autoCropArea="1"
            @ready="onCropperReady"
          />
        </div>

        <div class="buttons-container-modal flex width-all align-center space-around">
          <button class="btn big primary" @click="uploadFile">{{$t('send')}}</button>
          <button class="btn big" @click="clearBase64">{{$t('cancel')}}</button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import FileUploader from '@/apps/common/modules/common/cmps/file/FileUploader.vue';
import { alertService } from '@/apps/common/modules/common/services/alert.service';
import Modal from '@/apps/common/modules/common/cmps/Modal.vue';
import MiniLoader from '@/apps/common/modules/common/cmps/MiniLoader.vue';
import VueCropper from 'vue-cropperjs';
import 'cropperjs/dist/cropper.css';
import { fixFileSrcToThumbnail } from '@/apps/common/modules/common/services/file.service';
export default {
  components: { Modal, VueCropper, MiniLoader },
  extends: FileUploader,
  name: 'ImageCrop',
  props: {
    value: null, // [{ src, title }],
    onlySrc: null, // [{ src, title }],
    accept: String,
    uploadFolderName: String,
    parentData: Object,
    onupload: Function,
    rootItem: Object
  },
  data() {
    return {
      imgBase64ToCrop: '',
      fileName: '',
      cropperOptions: {
        viewMode: 2, // Restricts the minimum canvas size to fit within the container
        responsive: false, // Re-renders the cropper when resizing the window
        restore: false, // Restores the cropped area after resizing the window
        autoCropArea: 1
      }
    }
  },
  computed: {
    imgToShow() {
      // return fixFileSrcToThumbnail(this.onlySrc? this.value : this.value?.src || ''); // defaultImg
      return this.previewSrc || fixFileSrcToThumbnail(this.value, this.rootItem) || require('@/apps/megaphonApp/assets/images/upload_placeholder.svg'); // defaultImg
    }
  },
  watch: {
    previewSrc(newVal) {
      // When previewSrc changes (after upload), mark image as loading
      if (newVal) {
        this.imageLoading = true
      }
    },
    imgToShow(newVal) {
      // When imgToShow changes (value changed or previewSrc set), mark image as loading
      // Only if it's not the placeholder
      if (newVal && !newVal.includes('upload_placeholder.svg')) {
        this.imageLoading = true
      }
    },
    imgBase64ToCrop(newVal) {
      // When imgBase64ToCrop is set, ensure VueCropper initializes after modal is visible
      if (newVal) {
        this.$nextTick(() => {
          // Double nextTick to ensure modal is fully rendered and visible
          this.$nextTick(() => {
            // Verify cropper is initialized
            if (this.$refs.cropper && this.$refs.cropper.cropper) {
              // Force cropper to recalculate dimensions and set full crop area
              try {
                const cropper = this.$refs.cropper.cropper
                cropper.reset()
                // Wait for reset to complete, then set crop to full image
                setTimeout(() => {
                  try {
                    const imageData = cropper.getImageData()
                    cropper.setCropBoxData({
                      left: imageData.left,
                      top: imageData.top,
                      width: imageData.width,
                      height: imageData.height
                    })
                  } catch (err) {
                    console.warn('Failed to set full crop area in watcher:', err)
                  }
                }, 200)
              } catch (err) {
                console.warn('VueCropper reset failed:', err)
              }
            }
          })
        })
      }
    }
  },
  methods: { 
    validateImageFile(file) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve(true); // Valid image
        };
        
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Not a valid image file'));
        };
        
        img.src = url;
      });
    },
    async uploadFile() {
      const file = this.base64ToFile(this.getCroppedImg());
      if (!file) return;
      this.imgBase64ToCrop = '';
      const newVal = await this.doUploadFile(file, this.uploadFolderName, this.parentData);
      if (this.onlySrc) {
        this.$emit('input', newVal.src);
        if (this.onupload) this.onupload(newVal.src);
      }
      else {
        this.$emit('input', newVal);
        if (this.onupload) this.onupload(newVal);
      }
      this.imgBase64ToCrop = '';
    },
    async chooseFile() {
      const file = this.getFileFromInput(this.$refs.inputEl);
      if (!file) return;
      
      // Validate image content before processing
      try {
        await this.validateImageFile(file);
      } catch (error) {
        alertService.toast({
          type: 'danger', 
          msg: this.$t('invalidImageFileError')
        });
        return;
      }
      
      this.isLoading = true;
      try {
        this.imgBase64ToCrop = await this.fileToBase64(file);
        this.fileName = file.name;
      } catch (err) {
        alertService.toast({type: 'danger', msg: `cantUploadFileError`});
      }
      this.isLoading = false;
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });
    },
    base64ToFile(base64) {
      if (!base64) return null;
      function dataURLtoFile(dataurl, filename = 'file') {
          var arr = dataurl.split(','),
              mime = arr[0].match(/:(.*?);/)[1],
              bstr = atob(arr[arr.length - 1]), 
              n = bstr.length, 
              u8arr = new Uint8Array(n);
          while(n--){
              u8arr[n] = bstr.charCodeAt(n);
          }
          return new File([u8arr], filename, {type:mime});
      }
      return dataURLtoFile(base64, this.fileName);
    },

    clearBase64() {
      this.imgBase64ToCrop = '';
    },

    
    onCropperReady() {
      // Cropper is ready - ensure it's properly sized and crop box covers full image
      if (this.$refs.cropper && this.$refs.cropper.cropper) {
        try {
          const cropper = this.$refs.cropper.cropper
          // Reset to ensure proper initialization
          cropper.reset()
          
          // Wait a bit for the reset to complete, then set crop box to cover entire image
          this.$nextTick(() => {
            setTimeout(() => {
              try {
                // Get image data to know the actual image dimensions and position
                const imageData = cropper.getImageData()
                
                // Set crop box to cover entire image
                // The crop box should be positioned at the image's position within the container
                // and sized to match the image dimensions
                cropper.setCropBoxData({
                  left: imageData.left,
                  top: imageData.top,
                  width: imageData.width,
                  height: imageData.height
                })
              } catch (err) {
                console.warn('Failed to set full crop area:', err)
                // Fallback: try using crop() method to reset crop area
                try {
                  cropper.crop()
                } catch (fallbackErr) {
                  console.warn('Fallback crop() also failed:', fallbackErr)
                }
              }
            }, 150)
          })
        } catch (err) {
          console.warn('VueCropper ready reset failed:', err)
        }
      }
    },
    getCroppedImg() {
      if (!this.$refs.cropper || !this.$refs.cropper.cropper) {
        console.error('VueCropper not initialized')
        return null
      }
      try {
        const cropper = this.$refs.cropper.cropper
        const croppedCanvas = cropper.getCroppedCanvas()
        if (!croppedCanvas) {
          console.error('Failed to get cropped canvas')
          return null
        }
        const croppedImage = croppedCanvas.toDataURL()
        return croppedImage
      } catch (err) {
        console.error('Error getting cropped image:', err)
        return null
      }
    },
  }
}
</script>

<style lang="scss">
.megaphon-app {
  .img-crop-input {
    .buttons-container {
      min-width: em(110px);
      flex-shrink: 0;
    }
    .loader-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: em(80px);
      width: 100%;
      .mini-loader {
        width: em(50px);
        height: em(50px);
      }
    }
    .img-container {
      position: relative;
      display: inline-block;
    }
    .image-loader-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba(255, 255, 255, 0.8);
      z-index: 1;
    }
    .crop-modal {
      height: 100%;
      min-height: 0;
      overflow: hidden;
      
      .cropper-container {
        flex: 1;
        min-height: 0;
        max-height: calc(90vh - 150px);
        width: 100%;
        max-width: 90vw;
        margin: 0 auto;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        
        // Target cropperjs internal elements
        :deep(.cropper-container) {
          max-width: 100% !important;
          max-height: 100% !important;
        }
        
        :deep(.cropper-canvas) {
          max-width: 100% !important;
          max-height: 100% !important;
        }
        
        :deep(img) {
          max-width: 100% !important;
          max-height: 100% !important;
          object-fit: contain;
        }
      }
      
      .buttons-container-modal {
        flex-shrink: 0;
      }
      
      .btn {
        justify-content: center;
      }
    }
  }
}
</style>